// Lab results released to the patient: the chat card's text, its parser, the model placeholder and
// WF72's release decision. ONE source, spliced into every copy by scripts/sync-lab-results-card.mjs
// (S-173 compares them). The back office imports this file directly.
// Spec: docs/specs/2026-10-08-lab-results-to-patient.md. ASCII only: the regions are spliced into
// workflow JSON files that store non-ASCII as \u escapes. No lookbehind: the app bundle imports it.

// LAB-CARD-SHARED-START
const LAB_HEADER = 'Your lab results are ready.';
const LAB_PROVENANCE = 'Results from Quest Diagnostics, as reported by the lab.';
const LAB_NOT_A_PROBLEM = 'A result outside the range is not always a problem.';
const LAB_FOOTER = ['Your nurse practitioner reviews every result.', 'Questions? Reply to this message.'];
// A review marked Done before every specimen was final: the rest is counted, never named (a pending
// HIV or drug test must not be named on the card).
const LAB_PENDING_LINE = 'Some results are still in progress. Your care team will go over them with you.';
const LAB_MAX_RESULTS = 500;
const LAB_CAPS = { name: 60, value: 32, unit: 24, range: 40 };
const LAB_FLAGS = ['HIGH', 'LOW', 'OUTSIDE RANGE', 'in range'];
const LAB_NO_VALUE = ['not performed', 'see lab comment'];
// Section order is part of the grammar: the parser refuses any other order.
const LAB_SECTIONS = [
  { key: 'outside', label: "OUTSIDE THE LAB'S RANGE" },
  { key: 'plan', label: 'YOUR CARE PLAN LABS' },
  { key: 'heart', label: 'CHOLESTEROL AND HEART' },
  { key: 'sugar', label: 'BLOOD SUGAR' },
  { key: 'kidney', label: 'KIDNEY' },
  { key: 'liver', label: 'LIVER' },
  { key: 'minerals', label: 'ELECTROLYTES AND MINERALS' },
  { key: 'blood', label: 'BLOOD COUNT' },
  { key: 'thyroid', label: 'THYROID' },
  { key: 'vitamins', label: 'VITAMINS AND IRON' },
  { key: 'urine', label: 'URINE' },
  { key: 'inflammation', label: 'INFLAMMATION' },
  { key: 'other', label: 'OTHER' },
  { key: 'novalue', label: 'NO VALUE FROM THE LAB' },
];
// The four care-plan labs, by the LOINCs of scripts/elation-lab-parser.mjs (S-173 pins equality).
// The fixed name is how a renderer finds the goal: the body carries the name, not the LOINC.
const LAB_PLAN = [
  { key: 'hba1c', name: 'Hemoglobin A1c', loincs: ['4548-4', '17856-6'] },
  { key: 'ldl_c', name: 'LDL cholesterol', loincs: ['13457-7', '18262-6', '2089-1'] },
  { key: 'egfr', name: 'Kidney function (eGFR)', loincs: ['98979-8', '77147-7', '62238-1', '33914-3'] },
  { key: 'uacr', name: 'Urine albumin/creatinine (uACR)', loincs: ['14959-1', '9318-7', '32294-1', '30000-4'] },
];
// CKD-EPI 2009 race-specific eGFR: dropped when a race-free eGFR is on the order.
const LAB_RACE_EGFR = ['48642-3', '48643-1'];
const LAB_SYSTEM_LOINC = {
  heart: ['2093-3', '2085-9', '13457-7', '18262-6', '2089-1', '2571-8', '43396-1', '9830-1', '10835-7', '43583-4', '1884-6', '33762-6', '30934-4', '13965-9', '54434-6'],
  sugar: ['2345-7', '4548-4', '17856-6', '20448-7', '15069-8', '27353-2'],
  kidney: ['2160-0', '3094-0', '3097-3', '98979-8', '33914-3', '62238-1', '77147-7', '48642-3', '48643-1', '33863-2', '3084-1'],
  liver: ['1742-6', '1920-8', '6768-6', '1975-2', '1968-7', '1751-7', '2885-2', '10834-0', '1759-0', '2324-2'],
  minerals: ['2951-2', '2823-3', '2075-0', '2028-9', '17861-6', '19123-9', '2601-3', '2777-1', '33037-3'],
  blood: ['6690-2', '789-8', '718-7', '4544-3', '787-2', '785-6', '786-4', '788-0', '777-3', '32623-1', '751-8', '770-8', '731-0', '736-9', '742-7', '5905-5', '711-2', '713-8', '704-7', '706-2'],
  thyroid: ['3016-3', '11580-8', '3024-7', '3051-0', '3026-2', '8099-4'],
  vitamins: ['1989-3', '62292-8', '2132-9', '2284-8', '2498-4', '2500-7', '2276-4', '2502-3'],
  urine: ['14957-5', '2161-8', '14959-1', '9318-7', '32294-1', '30000-4', '5811-5', '5803-2', '5804-0', '5792-7', '5797-6', '5794-3', '5799-2', '5802-4', '5770-3'],
  inflammation: ['30522-7', '1988-5', '4537-7', '30341-2'],
};
// Name fallback, checked in this order (urine first: a urine glucose is a urine result).
const LAB_SYSTEM_NAME = [
  // Urinalysis analytes only: "Morphine, urine" must not be placed (and shown) as a urine result.
  ['urine', /urinalysis|specific gravity|leukocyte esterase|nitrite|^(albumin|creatinine|protein|glucose|ketones?|bilirubin|urobilinogen|ph|blood|occult blood|bacteria|rbc|wbc|red blood cells|white blood cells|squamous epithelial cells|epithelial cells|hyaline casts?|casts?|crystals|mucus|yeast|color|appearance|clarity|microalbumin)[, ]+(random )?urine$|microalbumin|albumin\/creatinine/i],
  ['heart', /cholest|\bhdl\b|\bldl\b|triglycer|lipoprotein|apolipo|\bbnp\b|homocyst/i],
  ['sugar', /glucose|a1c|insulin|fructosamine/i],
  ['kidney', /creatinine|\bbun\b|urea nitrogen|egfr|glomerular|cystatin|uric acid/i],
  ['liver', /\balt\b|\bast\b|alanine|aspartate|alkaline phos|bilirubin|albumin|protein|globulin|\bggt\b|gamma/i],
  ['minerals', /sodium|potassium|chloride|carbon dioxide|bicarbonate|\bco2\b|calcium|magnesium|phosph|anion gap/i],
  ['blood', /hemoglobin|hematocrit|platelet|\bwbc\b|white blood|\brbc\b|red blood|\bmcv\b|\bmch\b|\bmchc\b|\brdw\b|\bmpv\b|neutrophil|lymphocyte|monocyte|eosinophil|basophil/i],
  ['thyroid', /\btsh\b|thyro|\bt3\b|\bt4\b/i],
  ['vitamins', /vitamin|\bb12\b|folate|folic|ferritin|\biron\b|\btibc\b|transferrin/i],
  ['inflammation', /c-reactive|\bcrp\b|sedimentation|\besr\b/i],
];
const LAB_FRIENDLY = {
  '2093-3': 'Total cholesterol', '2085-9': 'HDL cholesterol', '2571-8': 'Triglycerides', '43396-1': 'Non-HDL cholesterol',
  '9830-1': 'Cholesterol/HDL ratio', '2345-7': 'Glucose', '2160-0': 'Creatinine', '3094-0': 'Urea nitrogen (BUN)',
  '3097-3': 'BUN/creatinine ratio', '3084-1': 'Uric acid', '1742-6': 'ALT', '1920-8': 'AST', '6768-6': 'Alkaline phosphatase',
  '1975-2': 'Bilirubin, total', '1751-7': 'Albumin', '2885-2': 'Total protein', '10834-0': 'Globulin',
  '1759-0': 'Albumin/globulin ratio', '2951-2': 'Sodium', '2823-3': 'Potassium', '2075-0': 'Chloride',
  '2028-9': 'Carbon dioxide', '17861-6': 'Calcium', '6690-2': 'White blood cells', '789-8': 'Red blood cells',
  '718-7': 'Hemoglobin', '4544-3': 'Hematocrit', '777-3': 'Platelets', '3016-3': 'TSH', '2132-9': 'Vitamin B12',
  '1989-3': 'Vitamin D, 25-hydroxy', '2276-4': 'Ferritin', '2498-4': 'Iron', '14957-5': 'Albumin, urine',
  '2161-8': 'Creatinine, urine', '30522-7': 'C-reactive protein, high sensitivity',
};
// Never shown in the card, only counted (spec D8: CA H&S 123148; the 72-hour holds of KY, TX, MT, VA).
const LAB_SENSITIVE = /phencyclidine|\bpcp\b|morphine|codeine|hydrocod|hydromorph|oxymorph|acetylmorphine|6-mam|heroin|tramadol|tapentadol|meperidine|propoxyphene|carisoprodol|mdma|methylenedioxy|ketamine|marijuana|ethanol|alcohol|\bcd[48]\b|electrophoresis|hemoglobin [sacf]\b|sickle|microglobulin|thyroglobulin|light chain|m-spike|monoclonal|immunofixation|\bca[- ]?27|hla-b|\bhiv\b|hiv-?[12]|immunodeficiency|hepatitis|\bhbsag\b|\bhbs\b|\bhcv\b|\bhbv\b|\bhav\b|\bhep [abc]\b|drug screen|toxicolog|opiate|opioid|amphetamine|cannabin|\bthc\b|cocaine|benzodiaz|fentanyl|methadone|oxycod|barbitur|buprenorph|glucuronide|genotyp|genetic|mutation|\bdna\b|\bapoe\b|\bhfe\b|factor v leiden|prothrombin gene|\bbrca|\bpsa\b|prostate.specific|\bcea\b|carcinoembryonic|\bca[- ]?125\b|\bca[- ]?19|\bca[- ]?15|fetoprotein|\bafp\b|tumor marker|chlamydia|gonorrh|neisseria|syphilis|\brpr\b|treponem|herpes|\bhsv\b|trichomon|\bhpv\b|papilloma|pregnan|\bhcg\b|chorionic/i;
// Known LOINCs for the same categories, checked before the name patterns.
const LAB_SENSITIVE_LOINC = ['56888-1', '75622-1', '7917-8', '48345-3', '13955-0', '5196-1', '16935-9', '22314-9', '11259-9', '20507-0', '5292-8', '2857-1', '10886-0', '2039-6', '10334-1', '24108-3', '1834-1', '43305-2', '43304-5', '21613-5', '2106-3', '19295-5', '3426-4', '8150-1', '3397-7', '18282-4', '5640-8'];
// Drug and alcohol tests, by name: whatever the matrix, they hide their whole specimen.
const LAB_DRUG = /drug screen|toxicolog|opiate|opioid|amphetamine|cannabin|\bthc\b|marijuana|cocaine|benzodiaz|barbitur|phencyclidine|\bpcp\b|methadone|oxycod|fentanyl|buprenorph|morphine|codeine|hydrocod|hydromorph|oxymorph|acetylmorphine|6-mam|heroin|tramadol|tapentadol|meperidine|propoxyphene|carisoprodol|mdma|methylenedioxy|ecstasy|ketamine|ethanol|alcohol|ethyl glucuronide|glucuronide/i;
function labIsSensitive(loinc, name) {
  return LAB_SENSITIVE_LOINC.indexOf(String(loinc || '').trim()) >= 0 || LAB_SENSITIVE.test(String(name || ''));
}
const LAB_ACRONYMS = ['LDL', 'HDL', 'ALT', 'AST', 'BUN', 'TSH', 'RBC', 'WBC', 'MCV', 'MCH', 'MCHC', 'RDW', 'MPV', 'CO2', 'T3', 'T4', 'B12', 'CRP', 'GGT', 'TIBC', 'BNP', 'A1C', 'EGFR', 'UACR'];
const LAB_NUM = /^-?\d+(\.\d+)?$/;
const LAB_CMP = /^[<>]=?\s?-?\d+(\.\d+)?$/;

// Fold to ASCII and strip the grammar's own delimiters, so lab text can never fake a line or a clause.
function labClean(s, cap) {
  let t = String(s == null ? '' : s)
    .replace(/[\u00b5\u03bc]/g, 'u').replace(/\u2265/g, '>=').replace(/\u2264/g, '<=')
    .replace(/[\u2013\u2014]/g, '-')
    .normalize('NFKD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^\x20-\x7e]/g, ' ')
    .replace(/[();:]/g, ' ')
    .replace(/\s+/g, ' ').trim();
  if (cap && t.length > cap) t = t.slice(0, cap).trim();
  return t;
}
function labUnit(u) {
  return labClean(String(u == null ? '' : u).replace(/\([^)]*\)/g, ' '), LAB_CAPS.unit);
}
function labName(raw, loinc) {
  const plan = LAB_PLAN.find((p) => p.loincs.indexOf(loinc) >= 0);
  if (plan) return plan.name;
  if (loinc && LAB_FRIENDLY[loinc]) return LAB_FRIENDLY[loinc];
  let n = labClean(raw, LAB_CAPS.name);
  if (n && !/[a-z]/.test(n)) {
    n = n.charAt(0) + n.slice(1).toLowerCase();
    for (const a of LAB_ACRONYMS) n = n.replace(new RegExp('\\b' + a + '\\b', 'gi'), a === 'EGFR' ? 'eGFR' : a);
  }
  return n;
}
function labSystem(loinc, name) {
  for (const k of Object.keys(LAB_SYSTEM_LOINC)) if (loinc && LAB_SYSTEM_LOINC[k].indexOf(loinc) >= 0) return k;
  for (const [k, re] of LAB_SYSTEM_NAME) if (re.test(name || '')) return k;
  return 'other';
}
// One reference bound as Quest sends it through Elation: an inclusive bound arrives as text ("< OR = 0.2",
// "> OR = 60") and a strict one ("<5.0") as a bare number, so a bare one-sided bound is strict.
function labLimit(s) {
  // A dash is stripped unless it is a minus sign.
  const t = labClean(s, LAB_CAPS.range).replace(/^(?:\s|-(?!\d))+|[-\s]+$/g, '');
  let m;
  if (!t || /^see (lab )?(note|comment)s?$/i.test(t)) return { raw: '' };
  if (LAB_NUM.test(t)) return { raw: t, n: t, bare: true };
  if ((m = /^(?:<\s*or\s*=|<=)\s*(-?\d+(?:\.\d+)?)$/i.exec(t))) return { raw: t, n: m[1], op: 'le' };
  if ((m = /^(?:>\s*or\s*=|>=)\s*(-?\d+(?:\.\d+)?)$/i.exec(t))) return { raw: t, n: m[1], op: 'ge' };
  if ((m = /^<\s*(-?\d+(?:\.\d+)?)$/.exec(t))) return { raw: t, n: m[1], op: 'lt' };
  if ((m = /^>\s*(-?\d+(?:\.\d+)?)$/.exec(t))) return { raw: t, n: m[1], op: 'gt' };
  return { raw: t };
}
// With the result's value and the lab's is_abnormal, a value sitting on a bare bound that the lab calls in range
// makes that bound inclusive: the flag is the only sign of which side the bound itself is on.
function labFormatRange(min, max, value, abnormal) {
  const a = labLimit(min), b = labLimit(max);
  if (a.bare && b.bare) return (a.n + ' to ' + b.n).slice(0, LAB_CAPS.range);
  const v = String(value == null ? '' : value).trim();
  const onBound = (l) => abnormal === false && LAB_NUM.test(v) && Number(v) === Number(l.n);
  const one = (l, bare, incl) => (l.bare ? (onBound(l) ? l.n + incl : bare + l.n) : l.op === 'le' ? l.n + ' or lower'
    : l.op === 'ge' ? l.n + ' or higher' : l.op === 'lt' ? 'below ' + l.n : 'above ' + l.n);
  if (!b.raw && a.n) return one(a, 'above ', ' or higher');
  if (!a.raw && b.n) return one(b, 'below ', ' or lower');
  if (a.raw && b.raw) return (a.raw === b.raw ? a.raw : a.raw + ' to ' + b.raw).slice(0, LAB_CAPS.range);
  return a.raw || b.raw || '';
}
// The limits a range sentence states, or null for the lab's own text. "below" and "above" exclude their bound.
function labRangeLimits(range) {
  const r = String(range || '');
  let m;
  if ((m = /^(-?\d+(?:\.\d+)?) to (-?\d+(?:\.\d+)?)$/.exec(r))) return { lo: Number(m[1]), hi: Number(m[2]), loOpen: false, hiOpen: false };
  if ((m = /^(-?\d+(?:\.\d+)?) or higher$/.exec(r))) return { lo: Number(m[1]), hi: null, loOpen: false, hiOpen: false };
  if ((m = /^(-?\d+(?:\.\d+)?) or lower$/.exec(r))) return { lo: null, hi: Number(m[1]), loOpen: false, hiOpen: false };
  if ((m = /^above (-?\d+(?:\.\d+)?)$/.exec(r))) return { lo: Number(m[1]), hi: null, loOpen: true, hiOpen: false };
  if ((m = /^below (-?\d+(?:\.\d+)?)$/.exec(r))) return { lo: null, hi: Number(m[1]), loOpen: false, hiOpen: true };
  return null;
}
const LAB_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function labDay(d) { const s = String(d || '').slice(0, 10); return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : ''; }
function labPrettyDay(day, withYear) {
  const [y, m, d] = day.split('-').map(Number);
  return LAB_MONTHS[m - 1] + ' ' + d + (withYear ? ', ' + y : '');
}
function labCollected(days) {
  const ds = days.filter(Boolean).sort();
  if (!ds.length) return null;
  const a = ds[0], b = ds[ds.length - 1];
  if (a === b) return labPrettyDay(a, true);
  if (a.slice(0, 4) === b.slice(0, 4)) return labPrettyDay(a, false) + ' to ' + labPrettyDay(b, true);
  return labPrettyDay(a, true) + ' to ' + labPrettyDay(b, true);
}
// Where a renderer may draw a range bar. Null unless the value and its bound(s) are plain numbers AND
// they agree with the lab's flag: a bar that contradicts the lab is worse than no bar.
function labBar(row) {
  if (!row || !LAB_NUM.test(String(row.value || '')) || !row.range) return null;
  const v = Number(row.value);
  const L = labRangeLimits(row.range);
  if (!L) return null;
  const { lo, hi } = L;
  if (lo != null && hi != null && lo > hi) return null;
  const above = hi != null && (L.hiOpen ? v >= hi : v > hi);
  const below = lo != null && (L.loOpen ? v <= lo : v < lo);
  const agrees = row.flag === 'in range' ? !above && !below
    : row.flag === 'HIGH' ? above
    : row.flag === 'LOW' ? below
    : false;
  if (!agrees) return null;
  let dmin, dmax;
  if (lo != null && hi != null) { const s = (hi - lo) || Math.abs(hi) || 1; dmin = Math.max(0, lo - s * 0.6); dmax = hi + s * 0.6; }
  else if (hi != null) { dmin = 0; dmax = Math.max(hi * 1.6, v * 1.15, 1); }
  else { dmin = 0; dmax = Math.max(lo * 2, v * 1.2, 1); }
  if (v < dmin) dmin = v; if (v > dmax) dmax = v;
  const pct = (x) => Math.max(3, Math.min(97, ((x - dmin) / ((dmax - dmin) || 1)) * 100));
  const bandFrom = lo != null ? pct(lo) : 0;
  const bandTo = hi != null ? pct(hi) : 100;
  return { bandFrom, bandTo, at: pct(v) };
}
function labPlanKey(name) { const p = LAB_PLAN.find((x) => x.name === name); return p ? p.key : null; }
// LAB-CARD-SHARED-END

// LAB-CARD-NOTE-START
// The care team's note on the card: cleaned, never cut (over a limit it is refused whole). WF17, WF72,
// the app and the companion run this same code.
const LAB_NOTE_HEADING = 'NOTE FROM YOUR CARE TEAM';
const LAB_NOTE_LIMITS = { chars: 600, lines: 12, name: 40 };
function labNoteClean(raw) {
  if (typeof raw !== 'string') return '';
  // Lone surrogates go, by hand: no lookbehind (the companion's iOS webview cannot compile one).
  let s = '';
  for (let i = 0; i < raw.length; i++) {
    const c = raw.charCodeAt(i);
    if (c >= 0xD800 && c <= 0xDBFF) {
      const d = raw.charCodeAt(i + 1);
      if (d >= 0xDC00 && d <= 0xDFFF) { s += raw[i] + raw[i + 1]; i += 1; }
      continue;
    }
    if (c >= 0xDC00 && c <= 0xDFFF) continue;
    s += raw[i];
  }
  s = s.replace(/\r\n?|[\u0085\u2028\u2029]/g, '\n').replace(/\t/g, ' ')
    .replace(/[\p{Cc}\p{Cf}\p{Co}\u115F\u1160\u2800\u3164\uFFA0\uFFFC]/gu, (ch) => (ch === '\n' ? ch : ''))
    .replace(/(\p{M}{3})\p{M}+/gu, '$1');
  // trimEnd, not /\s+$/: the regex is quadratic on a long run of spaces mid-line (one request could stall n8n).
  s = s.split('\n').map((l) => l.trimEnd()).join('\n').replace(/\n{3,}/g, '\n\n').trim();
  // A note with no letter or digit is not a note (invisible characters only).
  return /[\p{L}\p{N}]/u.test(s) ? s : '';
}
// The limits first, so a huge input is refused before it is cleaned.
function labNoteOk(text) {
  if (typeof text !== 'string' || !text || text.length > LAB_NOTE_LIMITS.chars) return false;
  return text.split('\n').length <= LAB_NOTE_LIMITS.lines && labNoteClean(text) === text;
}
// The patient sees the author's first name only: NFC, NFKC-stable, cased Latin letters, spaces, ' and -; else none.
function labNoteName(firstname) {
  if (typeof firstname !== 'string') return null;
  const n = firstname.trim().normalize('NFC');
  return n.length <= LAB_NOTE_LIMITS.name && n.normalize('NFKC') === n
    && /^[\p{Lu}\p{Ll}][\p{Lu}\p{Ll}' -]*$/u.test(n)
    && !/[^\p{Script=Latin}' -]/u.test(n) && n.indexOf('  ') < 0 ? n : null;
}
// LAB-CARD-NOTE-END

// LAB-CARD-PROJECT-START
// Collect Lab Reports -> release_grids: every live Lab report's grids, results kept raw but bounded.
// Order scoping happens downstream (Decide knows the order id). Never throws.
const LAB_PROJECT_CAPS = { grids: 40, results: 600 };
function projectReleaseGrids(reports, ehrId, orderIds) {
  const ours = new Set((Array.isArray(orderIds) ? orderIds : []).map(String).filter(Boolean));
  const out = { grids: [], truncated: false, foreign: false };
  // No chart id means no way to prove whose reports these are: release nothing.
  if (!ehrId) { out.foreign = true; return out; }
  let nres = 0;
  const str = (v, n) => (v == null ? '' : String(v)).slice(0, n);
  for (const r of (Array.isArray(reports) ? reports : [])) {
    if (!r || String(r.report_type || '') !== 'Lab') continue;
    if (r.deleted_date && String(r.deleted_date) !== 'None') continue;
    // Another patient's report on this page means the filter did not apply (class #20): hold, never skip.
    if (String(r.patient == null ? '' : r.patient) !== String(ehrId)) { out.foreign = true; return out; }
    // Only reports on one of the patient's own Elation orders travel on: history stays behind.
    if (r.requisition_number == null || !ours.has(String(r.requisition_number))) continue;
    for (const g of (Array.isArray(r.grids) ? r.grids : [])) {
      if (out.grids.length >= LAB_PROJECT_CAPS.grids) { out.truncated = true; break; }
      const results = [];
      for (const x of (Array.isArray(g && g.results) ? g.results : [])) {
        if (nres >= LAB_PROJECT_CAPS.results) { out.truncated = true; break; }
        nres += 1;
        const t = (x && x.test) || {};
        const note = (x && x.note != null && String(x.note).trim() !== '') || (x && x.text != null && String(x.text).trim() !== '');
        results.push({
          name: str(t.name, 120),
          loinc: t.loinc == null ? '' : str(t.loinc, 16),
          value: str(x && x.value, 64),
          units: str(x && x.units, 40),
          ref_min: str(x && x.reference_min, 64),
          ref_max: str(x && x.reference_max, 64),
          is_abnormal: x && typeof x.is_abnormal === 'boolean' ? x.is_abnormal : null,
          abnormal_flag: str(x && x.abnormal_flag, 80),
          has_note: !!note,
        });
      }
      out.grids.push({
        requisition_number: r.requisition_number == null ? null : String(r.requisition_number),
        report_id: String(r.id == null ? '' : r.id),
        status: String((g && g.status) || '').toUpperCase(),
        accession: g && g.accession_number != null ? String(g.accession_number) : null,
        collected_date: g && g.collected_date != null ? String(g.collected_date) : null,
        resulted_date: g && g.resulted_date != null ? String(g.resulted_date) : null,
        tests: results.slice(0, 12).map((x) => x.name.slice(0, 64)).filter(Boolean),
        results,
      });
    }
  }
  return out;
}
// LAB-CARD-PROJECT-END

// LAB-CARD-MARKER-START
// Rev 4.1: the release is requested when the care team marks the review Done (WF17 writes
// context_json.lab_release in that same PATCH) and the marker moves one way from there. WF17, WF72's
// awaiting set and Decide Release all splice this region, so they apply one rule.
const LAB_RELEASE_WINDOW_MS = 72 * 3600000;
const LAB_RELEASE_STATES = ['requested', 'held', 'released', 'expired'];
// 'conflict' is no longer written (a tie now leaves one test off), but markers written before still carry it.
const LAB_HOLD_REASONS = ['critical_open', 'critical_unreviewed', 'critical_read_degraded', 'no_consent', 'guard_degraded',
  'foreign_report', 'truncated', 'conflict', 'flag_missing', 'empty', 'nothing_final', 'unnamed', 'too_many', 'unparseable',
  'order_cancelled', 'no_grids', 'window'];
// Whether $vars.LAB_RESULTS_RELEASE releases to this patient ('live'), only counts ('plan') or does
// nothing ('off'). Exact strings. On any program but PROD a bare 'true' only counts: BETA reads PROD's
// Elation charts, so a pilot list is the only live BETA mode.
function labReleaseModeFor(varValue, programid, uid) {
  const v = String(varValue == null ? '' : varValue);
  if (/^only:\d+(,\d+)*$/.test(v)) return v.slice(5).split(',').indexOf(String(uid)) >= 0 ? 'live' : 'off';
  if (v === 'true') return String(programid == null ? '' : programid).trim() === '6720' ? 'live' : 'plan';
  if (v === 'plan') return 'plan';
  return 'off';
}
// apiv2 keeps completed_at to the second, so two clocks are compared to the second.
function labSameInstant(a, b) {
  const x = Date.parse(String(a || '')), y = Date.parse(String(b || ''));
  return Number.isFinite(x) && Number.isFinite(y) && Math.floor(x / 1000) === Math.floor(y / 1000);
}
const LAB_ISO = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,3})?Z$/;
// The tests a released card left off (two values the lab did not tell apart), named for the care team.
const LAB_WITHHELD_MAX = 10;
const LAB_WITHHELD_NAME = /^(?=.*[A-Za-z0-9])[\x20-\x39\x3c-\x7e]{1,60}$/;
// The closed shape: anything else is refused by WF17 and ignored by WF72.
function labReleaseMarkerOk(m) {
  if (!m || typeof m !== 'object' || Array.isArray(m)) return false;
  const keys = Object.keys(m);
  if (keys.some((k) => ['state', 'since', 'order', 'reports', 'reason', 'at', 'note_at', 'withheld'].indexOf(k) < 0)) return false;
  if (LAB_RELEASE_STATES.indexOf(m.state) < 0 || typeof m.since !== 'string' || !LAB_ISO.test(m.since)) return false;
  if (m.at !== undefined && (typeof m.at !== 'string' || !LAB_ISO.test(m.at))) return false;
  // WF72's report that the care team's note rode the card: only on the released marker.
  if (m.note_at !== undefined && (m.state !== 'released' || typeof m.note_at !== 'string' || !LAB_ISO.test(m.note_at))) return false;
  if (m.withheld !== undefined && (m.state !== 'released' || !Array.isArray(m.withheld) || !m.withheld.length
    || m.withheld.length > LAB_WITHHELD_MAX || !m.withheld.every((s, i) => typeof s === 'string' && LAB_WITHHELD_NAME.test(s) && m.withheld.indexOf(s) === i))) return false;
  if (m.order !== undefined && !/^\d{1,20}$/.test(String(m.order))) return false;
  if (m.reason !== undefined && LAB_HOLD_REASONS.indexOf(m.reason) < 0) return false;
  if (m.reports !== undefined && !(Array.isArray(m.reports) && m.reports.length <= 40 && m.reports.every((r) => /^\d{1,20}$/.test(String(r))))) return false;
  if (m.state === 'requested' && keys.some((k) => k !== 'state' && k !== 'since')) return false;
  if (m.state === 'released' && (m.order === undefined || !Array.isArray(m.reports) || !m.reports.length)) return false;
  if ((m.state === 'held' || m.state === 'expired') && m.reason === undefined) return false;
  return true;
}
// One way only, and only for THIS Done: a marker whose clock is not the row's completed_at is stale.
function labReleaseNextOk(prev, next, completedAt) {
  if (!labReleaseMarkerOk(prev) || !labReleaseMarkerOk(next)) return false;
  if (!labSameInstant(prev.since, completedAt) || prev.since !== next.since) return false;
  const allowed = { requested: ['held', 'released', 'expired'], held: ['held', 'released', 'expired'], released: [], expired: [] };
  return allowed[prev.state].indexOf(next.state) >= 0;
}
// The request a completed review carries, or null. Kept a little past the window so Decide Release
// can write `expired` once.
function labReleaseRequestOf(review, nowMs) {
  if (!review || String(review.status || '') !== 'completed') return null;
  let ctx = review.context_json;
  if (typeof ctx === 'string') { try { ctx = JSON.parse(ctx); } catch (e) { ctx = null; } }
  const m = ctx && typeof ctx === 'object' ? ctx.lab_release : null;
  if (!labReleaseMarkerOk(m) || m.state === 'expired' || !labSameInstant(m.since, review.completed_at)) return null;
  const age = nowMs - Date.parse(m.since);
  if (!Number.isFinite(age) || age < -300000 || age > LAB_RELEASE_WINDOW_MS + 7200000) return null;
  return m;
}
// context_json.lab_note, written by WF17 only: { text, by?, at, sent_at? }. `at` is the completed_at of
// the Done that wrote it; `sent_at` is set once, when WF72 reports the note rode the card.
const LAB_NOTE_KEYS = ['text', 'by', 'at', 'sent_at'];
function labNoteStoredOk(n) {
  if (!n || typeof n !== 'object' || Array.isArray(n)) return false;
  if (Object.keys(n).some((k) => LAB_NOTE_KEYS.indexOf(k) < 0)) return false;
  if (!labNoteOk(n.text) || typeof n.at !== 'string' || !LAB_ISO.test(n.at)) return false;
  if (n.sent_at !== undefined && (typeof n.sent_at !== 'string' || !LAB_ISO.test(n.sent_at))) return false;
  return n.by === undefined || (!!n.by && typeof n.by === 'object' && !Array.isArray(n.by));
}
// A sent note never changes again; WF17 reads this on the row BEFORE it rewrites the marker.
function labNoteFrozen(ctx) {
  if (typeof ctx === 'string') { try { ctx = JSON.parse(ctx); } catch (e) { ctx = null; } }
  const n = ctx && typeof ctx === 'object' ? ctx.lab_note : null;
  return !!(n && typeof n === 'object' && n.sent_at);
}
// The note that counts: a sent one always (the patient has it), an unsent one only for THIS Done.
function labNoteCurrent(review) {
  if (!review) return null;
  let ctx = review.context_json;
  if (typeof ctx === 'string') { try { ctx = JSON.parse(ctx); } catch (e) { ctx = null; } }
  const n = ctx && typeof ctx === 'object' ? ctx.lab_note : null;
  if (!labNoteStoredOk(n)) return null;
  if (n.sent_at) return n;
  return String(review.status || '') === 'completed' && labSameInstant(n.at, review.completed_at) ? n : null;
}
// The note that may go out with the card of the Done at `since`: never sent, and written by that Done.
function labNoteForRelease(note, since) {
  return labNoteStoredOk(note) && !note.sent_at && labSameInstant(note.at, since) ? note : null;
}
// What the card carries: the text and, when it passes the name rule, the first name.
function labNoteModelOf(n) {
  if (!labNoteStoredOk(n)) return null;
  const name = labNoteName(n.by && n.by.firstname);
  return name ? { name, text: n.text } : { text: n.text };
}
// WF17's stamp for a released marker that reports THIS note: the moment it went, or null.
function labNoteSentAt(note, marker) {
  if (!labNoteStoredOk(note) || note.sent_at || !labReleaseMarkerOk(marker) || marker.state !== 'released') return null;
  if (marker.note_at !== note.at || !labSameInstant(marker.since, note.at)) return null;
  return marker.at || marker.note_at;
}
// LAB-CARD-MARKER-END

// LAB-CARD-MODEL-START
// Scoped grids -> the card model, or a hold. The lab decides every flag; we only read it.

// Vital signs Quest prints beside the results are not lab results: left off the card and not counted.
// Its HEIGHT (FT) and HEIGHT (IN) share 3138-5, which read as one test with two values.
const LAB_VITALS_LOINC = ['8302-2', '3137-7', '3138-5', '29463-7', '3141-9', '3142-7', '39156-5', '8480-6', '8462-4', '55284-4',
  '85354-9', '8867-4', '8310-5', '9279-1', '59408-5', '8280-0', '56086-2'];
const LAB_VITALS = /^(height|weight|body (height|weight|mass index|temperature)|bmi|bp|blood pressure|systolic|diastolic|pulse|heart rate|respiratory rate|respirations?|temperature|waist( circumference)?|oxygen saturation|spo2)(,? (systolic|diastolic))?( (ft|in|inches|cm|m|lbs?|kg|kg\/m2|mmhg|bpm|beats\/min|breaths\/min|f|c|%))?$/i;
// A code we know as a lab result is never a vital, whatever its name says.
function labKnownLoinc(l) {
  return !!l && (!!LAB_FRIENDLY[l] || LAB_SENSITIVE_LOINC.indexOf(l) >= 0 || LAB_PLAN.some((p) => p.loincs.indexOf(l) >= 0)
    || Object.keys(LAB_SYSTEM_LOINC).some((k) => LAB_SYSTEM_LOINC[k].indexOf(l) >= 0)
    || Object.keys(LAB_CRITICAL).some((k) => LAB_CRITICAL[k].loinc.indexOf(l) >= 0));
}
function labIsVital(loinc, name) {
  const l = String(loinc || '').trim(), n = labClean(name);
  if (LAB_VITALS_LOINC.indexOf(l) >= 0) return !n || LAB_VITALS.test(n);
  return !labKnownLoinc(l) && LAB_VITALS.test(n);
}
// Raw AND cleaned name: an accent or a full-width letter must not slip a sensitive test past the check.
function labSensitiveRow(loinc, name) {
  return labIsSensitive(loinc, name) || LAB_SENSITIVE.test(labClean(name));
}

// WF72's critical-value table (Collect Lab Reports, S131-PINNED), applied to the order's own grids
// WITHOUT its 14-day freshness window: a critical that was never alerted must not reach the patient
// first. S-173 runs both copies on the same values.
const LAB_CRITICAL = {
  potassium: { loinc: ['2823-3'], units: ['meq/l', 'mmol/l'], low: ['<=', 2.8], high: ['>=', 6.0] },
  sodium: { loinc: ['2951-2'], units: ['meq/l', 'mmol/l'], low: ['<=', 120], high: ['>=', 158] },
  glucose: { loinc: ['2345-7'], units: ['mg/dl'], low: ['<=', 46], high: ['>=', 484] },
  calcium: { loinc: ['17861-6'], units: ['mg/dl'], low: ['<=', 6.6], high: ['>=', 12.9] },
  bicarbonate: { loinc: ['2028-9'], units: ['meq/l', 'mmol/l'], low: ['<', 15], high: ['>', 40] },
  creatinine: { loinc: ['2160-0'], units: ['mg/dl'], low: null, high: ['>', 10] },
  chloride: { loinc: ['2075-0'], units: ['meq/l', 'mmol/l'], low: ['<', 82], high: ['>', 121] },
  // Signed off by NB (clinical lead), 2026-10-09: Quest's adult Priority 1 values (QDMOQ372 v20, 2025-03-31).
  hemoglobin: { loinc: ['718-7'], units: ['g/dl'], low: ['<=', 6.0], high: ['>=', 22.5] },
  platelets: { loinc: ['777-3'], units: ['thousand/ul', 'x10e3/ul', 'k/ul', '10*3/ul', '10^3/ul'], low: ['<', 20], high: ['>=', 2000] },
  wbc: { loinc: ['6690-2'], units: ['thousand/ul', 'x10e3/ul', 'k/ul', '10*3/ul', '10^3/ul'], low: null, high: ['>', 200] },
  neutrophils: { loinc: ['751-8'], units: ['cells/ul'], low: ['<', 500], high: null },
  inr: { loinc: ['6301-6', '34714-6'], units: ['', 'ratio', 'inr', '{inr}'], low: null, high: ['>=', 5.0] },
  magnesium: { loinc: ['19123-9'], units: ['mg/dl'], low: ['<=', 1.0], high: ['>=', 6.1] },
  phosphate: { loinc: ['2777-1'], units: ['mg/dl'], low: ['<=', 1.0], high: null },
};
const labCmp = (op, a, b) => (op === '<=' ? a <= b : op === '<' ? a < b : op === '>=' ? a >= b : a > b);
const labNormUnit = (u) => String(u || '').trim().toLowerCase().replace(/\([^)]*\)/g, ' ').replace(/\bcreat(inine)?\b/g, ' ').replace(/\s+/g, ' ').trim();
// 'high' | 'low' | null, by WF72's rules: LOINC required, unit must match, ">600" / "<5" read too.
function labCriticalVerdict(x) {
  const loinc = String((x && x.loinc) || '').trim();
  const key = Object.keys(LAB_CRITICAL).find((k) => LAB_CRITICAL[k].loinc.indexOf(loinc) >= 0);
  if (!key) return null;
  const c = LAB_CRITICAL[key];
  // A critical analyte we cannot read is never released unreviewed.
  if (c.units.indexOf(labNormUnit(x.units)) < 0) return 'unverifiable';
  const raw = String(x.value == null ? '' : x.value).slice(0, 32).trim();
  const n = Number(raw);
  if (raw !== '' && Number.isFinite(n)) {
    if (c.low && labCmp(c.low[0], n, c.low[1])) return 'low';
    if (c.high && labCmp(c.high[0], n, c.high[1])) return 'high';
    return null;
  }
  const m = /^\s*([<>])\s*(\d+(?:\.\d+)?)\s*$/.exec(raw);
  if (!m) return raw === '' ? null : 'unverifiable';
  if (m[1] === '>' && c.high && labCmp(c.high[0], Number(m[2]), c.high[1])) return 'high';
  if (m[1] === '<' && c.low && labCmp(c.low[0], Number(m[2]), c.low[1])) return 'low';
  // ">4" potassium: not normal, not provably critical either.
  return 'unverifiable';
}
const LAB_CRITICAL_PHRASE = /critical|panic|\bcrit\b|^\s*(hh|ll|aa)\s*$/i;
function labValueKind(v) {
  const s = String(v == null ? '' : v).trim();
  // A titer (1:160) or a value with brackets would be changed by stripping the grammar's delimiters.
  if (/[:;()]/.test(s)) return { kind: 'novalue', novalue: 'see lab comment' };
  if (!s) return { kind: 'novalue', novalue: 'not performed' };
  if (/^(see note|note|comment|see comment|see below|see report|see lab comment|pending)$/i.test(s)) return { kind: 'novalue', novalue: 'see lab comment' };
  if (/^(tnp|qns|dnr|test not performed|not performed|cancell?ed|not done)$/i.test(s)) return { kind: 'novalue', novalue: 'not performed' };
  const c = labClean(s, 64);
  if (!c || c.length > LAB_CAPS.value) return { kind: 'novalue', novalue: 'see lab comment' };
  if (LAB_NUM.test(c)) return { kind: 'numeric', value: c };
  if (LAB_CMP.test(c)) return { kind: 'comparator', value: c.replace(/^([<>]=?)\s/, '$1') };
  // "2 to 5" would read back as the number 2 with the unit "to 5": not a value we can show as sent.
  if (/^(?:[<>]=?)?-?\d+(?:\.\d+)? /.test(c)) return { kind: 'novalue', novalue: 'see lab comment' };
  return { kind: 'text', value: c };
}
function labFlagOf(x, valueKind) {
  if (x.is_abnormal === false) return 'in range';
  if (x.is_abnormal !== true) return null;
  // The lab's own H/L wording decides the direction; the printed limits only fill in when it gives none.
  const ph = String(x.abnormal_flag || '');
  if (/above|high|^\s*h\s*$/i.test(ph)) return 'HIGH';
  if (/below|low|^\s*l\s*$/i.test(ph)) return 'LOW';
  // The same limits the card prints: 5.0 against "below 5.0" is HIGH. A backwards range decides nothing.
  const L = labRangeLimits(labFormatRange(x.ref_min, x.ref_max)) || { lo: null, hi: null };
  const { lo, hi } = L;
  if (lo == null || hi == null || lo <= hi) {
    if (valueKind.kind === 'numeric') {
      const v = Number(valueKind.value);
      if (hi != null && (L.hiOpen ? v >= hi : v > hi)) return 'HIGH';
      if (lo != null && (L.loOpen ? v <= lo : v < lo)) return 'LOW';
    }
    if (valueKind.kind === 'comparator') {
      const m = /^([<>])=?(-?\d+(?:\.\d+)?)$/.exec(valueKind.value);
      if (m && m[1] === '>' && hi != null && Number(m[2]) >= hi) return 'HIGH';
      if (m && m[1] === '<' && lo != null && Number(m[2]) <= lo) return 'LOW';
    }
  }
  return 'OUTSIDE RANGE';
}
// The reports a Done could have covered, frozen into the first marker so a retry never adds more.
function labReportsAt(grids, doneAt) {
  const d = labDay(doneAt);
  if (!d) return [];
  return Array.from(new Set((grids || []).filter((g) => (g.status === 'FINAL' || g.status === 'CORRECTED')
    && labDay(g.resulted_date) !== '' && labDay(g.resulted_date) <= d)
    .map((g) => String(g.report_id || '')).filter((r) => /^\d{1,20}$/.test(r)))).sort().slice(0, 40);
}
function buildReleaseModel(input) {
  const all = (input && input.grids) || [];
  const stats = { results: 0, shown: 0, outside: 0, hidden: 0, sensitive: 0, unplaced: 0, unplaced_loincs: [], novalue: 0, vitals: 0, withheld: 0, withheld_tests: [], critical: false, ranges: { both: 0, min: 0, max: 0, text: 0, none: 0, at_bound: 0 }, flags: { numeric: 0, phrase: 0, generic: 0 }, groups: {}, pending: false, reports: [] };
  const hold = (reason) => ({ ok: false, reason, stats, critical: stats.critical });
  if (input && input.foreign) return hold('foreign_report');
  if (input && input.truncated) return hold('truncated');
  if (!all.length) return hold('empty');
  // Rev 4.1: with a Done time, only what was final by then is shown and the rest is counted as pending.
  // Without one (rev 3 callers) every specimen must be final.
  const doneDay = input && input.doneAt ? labDay(input.doneAt) : '';
  if (!doneDay && all.some((g) => g.status !== 'FINAL' && g.status !== 'CORRECTED')) return hold('not_final');
  const onlyReports = input && Array.isArray(input.reports) && input.reports.length ? input.reports.map(String) : null;
  // A drug screen hides its whole specimen, across every grid of it whatever its status: the
  // specimen's creatinine, pH and specific gravity are the screen's validity tests and would reveal
  // it. Judged on the specimen's names AND codes, since a screen may carry neither word nor 'urine'.
  const accOf = (g) => (g.accession ? 'a:' + g.accession : 'r:' + g.report_id);
  const accNamed = new Map();
  for (const g of all) {
    const list = accNamed.get(accOf(g)) || [];
    for (const t of (g.tests || [])) list.push({ loinc: '', name: String(t || '') });
    for (const x of (g.results || [])) list.push({ loinc: String(x.loinc || '').trim(), name: String(x.name || '') });
    accNamed.set(accOf(g), list);
  }
  const hideAcc = new Set();
  for (const [k, list] of accNamed) {
    const urineCoded = list.some((t) => LAB_SYSTEM_LOINC.urine.indexOf(t.loinc) >= 0);
    if (list.some((t) => (labSensitiveRow(t.loinc, t.name) && /urine/i.test(t.name + ' ' + labClean(t.name))) || LAB_DRUG.test(t.name) || LAB_DRUG.test(labClean(t.name))
      || (LAB_SENSITIVE_LOINC.indexOf(t.loinc) >= 0 && urineCoded))) hideAcc.add(k);
  }
  // What the care team could have reviewed: a FINAL/CORRECTED specimen filed on or before the Done day.
  // A specimen with no filing date was not provably there, so it waits.
  const usable = (g) => (g.status === 'FINAL' || g.status === 'CORRECTED')
    && (!doneDay || (labDay(g.resulted_date) !== '' && labDay(g.resulted_date) <= doneDay))
    && (!onlyReports || onlyReports.indexOf(String(g.report_id)) >= 0);
  const src = all.filter(usable);
  // Pending: anything on the order not shown yet, except a cancelled specimen (it will never arrive).
  stats.pending = !!doneDay && all.some((g) => !usable(g) && String(g.status || '').toUpperCase() !== 'CANCELLED' && String(g.status || '').toUpperCase() !== 'CANCELED');
  if (!src.length) return hold('nothing_final');
  stats.reports = Array.from(new Set(src.map((g) => String(g.report_id || '')).filter((r) => /^\d{1,20}$/.test(r)))).sort();
  // Grids: WF72's own rule - CORRECTED supersedes FINAL for the same specimen, else the newer report.
  const bySpecimen = new Map();
  for (const g of src) {
    const key = g.accession ? 'a:' + g.accession + '|' + (g.tests || []).join(',') : 'r:' + g.report_id + '|' + g.status;
    const prev = bySpecimen.get(key);
    // CORRECTED supersedes FINAL whatever the report order; otherwise the newer report (by number).
    const gc = g.status === 'CORRECTED', pc = prev && prev.status === 'CORRECTED';
    const better = !prev || (gc && !pc) || (gc === pc && Number(g.report_id) > Number(prev.report_id));
    if (better) bySpecimen.set(key, g);
  }
  // Results: one per test. CORRECTED, then the newer draw, then the newer report wins; an identical
  // value is kept once; two different values at the same rank cannot be told apart, so that test stays off.
  const byTest = new Map();
  const days = [];
  const hiddenKeys = new Set();
  for (const g of bySpecimen.values()) {
    const day = labDay(g.collected_date);
    const res = g.results || [];
    const hideGrid = hideAcc.has(accOf(g));
    for (const x of res) {
      const loinc = String(x.loinc || '').trim();
      const key = loinc || 'n:' + labClean(x.name).toLowerCase();
      if (key === 'n:') continue;
      if (hideGrid) { if (!hiddenKeys.has(key)) { hiddenKeys.add(key); stats.sensitive += 1; } continue; }
      if (labCriticalVerdict(x) || LAB_CRITICAL_PHRASE.test(String(x.abnormal_flag || ''))) stats.critical = true;
      if (labIsVital(loinc, x.name)) { stats.vitals += 1; continue; }
      // A vitals code under a lab's name is neither shown nor dropped silently: it counts with the hidden ones.
      if (LAB_VITALS_LOINC.indexOf(loinc) >= 0) { if (!hiddenKeys.has(key)) { hiddenKeys.add(key); stats.unplaced += 1; stats.unplaced_loincs.push(loinc); } continue; }
      // Sensitive tests leave first: no dedupe, no flag and no count reveals anything about them.
      if (labSensitiveRow(loinc, x.name)) { if (!hiddenKeys.has(key)) { hiddenKeys.add(key); stats.sensitive += 1; } continue; }
      const rank = [g.status === 'CORRECTED' ? 1 : 0, day, Number(g.report_id) || 0];
      const cand = { x, loinc, rank, corrected: g.status === 'CORRECTED', day };
      const list = byTest.get(key) || [];
      list.push(cand);
      byTest.set(key, list);
    }
  }
  const cmp = (a, b) => (a.rank[0] - b.rank[0]) || (a.rank[1] < b.rank[1] ? -1 : a.rank[1] > b.rank[1] ? 1 : 0) || (a.rank[2] - b.rank[2]);
  const picked = [];
  const tied = [];
  for (const list of byTest.values()) {
    list.sort((a, b) => cmp(b, a));
    const top = list[0];
    const tie = list.find((c, i) => i > 0 && cmp(c, top) === 0 && String(c.x.value).trim() !== String(top.x.value).trim());
    if (tie) { tied.push(top); continue; }
    picked.push(top);
  }
  // One row per plan lab: calculated and direct LDL, or two eGFR equations, would show the patient two
  // rows with the same name. Keep the parser's preferred code (its LOINC order) and drop the others.
  for (const plan of LAB_PLAN) {
    const mine = picked.filter((p) => plan.loincs.indexOf(p.loinc) >= 0);
    if (mine.length < 2) continue;
    mine.sort((a, b) => plan.loincs.indexOf(a.loinc) - plan.loincs.indexOf(b.loinc));
    for (const extra of mine.slice(1)) picked.splice(picked.indexOf(extra), 1);
  }
  // A tied race-free eGFR still drops the race-based one: it is the result the patient is owed, unseen or not.
  const hasRaceFree = picked.concat(tied).some((p) => LAB_PLAN[2].loincs.indexOf(p.loinc) >= 0);
  // A tied test the card would have dropped anyway (race-based eGFR, a second code for a shown plan lab) is not counted.
  // Two tied codes of one plan lab are one row the card would have shown: counted once.
  const tiedPlans = new Set();
  const withheld = tied.filter((c) => {
    if (hasRaceFree && LAB_RACE_EGFR.indexOf(c.loinc) >= 0) return false;
    const plan = LAB_PLAN.find((q) => q.loincs.indexOf(c.loinc) >= 0);
    if (!plan) return true;
    if (picked.some((p) => plan.loincs.indexOf(p.loinc) >= 0) || tiedPlans.has(plan.key)) return false;
    tiedPlans.add(plan.key);
    return true;
  });
  stats.withheld = withheld.length;
  let wn = Array.from(new Set(withheld.map((c) => { const n = labName(c.x.name, c.loinc); return LAB_WITHHELD_NAME.test(n) ? n : 'Unnamed test'; }))).sort();
  // Every left-off test is accounted for: past the cap, the last entry counts the rest.
  if (wn.length > LAB_WITHHELD_MAX) wn = wn.slice(0, LAB_WITHHELD_MAX - 1).concat([(wn.length - LAB_WITHHELD_MAX + 1) + ' more']);
  stats.withheld_tests = wn;
  const sections = {};
  for (const s of LAB_SECTIONS) sections[s.key] = [];
  let hidden = 0;
  for (const p of picked) {
    const x = p.x;
    if (hasRaceFree && LAB_RACE_EGFR.indexOf(p.loinc) >= 0) continue;
    stats.results += 1;
    const name = labName(x.name, p.loinc);
    if (!name) return hold('unnamed');
    if (LAB_SENSITIVE.test(name)) { hidden += 1; stats.sensitive += 1; continue; }
    const plan = LAB_PLAN.find((q) => q.loincs.indexOf(p.loinc) >= 0);
    // Each name on its own: the urine allow-list is anchored to the whole name.
    const sys1 = plan ? 'plan' : labSystem(p.loinc, name);
    const system = sys1 !== 'other' ? sys1 : labSystem(p.loinc, labClean(x.name).toLowerCase());
    // Only results we can place in a body system are shown; the rest are counted, not named (spec D8).
    if (system === 'other') { hidden += 1; stats.unplaced += 1; if (p.loinc) stats.unplaced_loincs.push(p.loinc); continue; }
    if (p.day) days.push(p.day);
    const vk = labValueKind(x.value);
    if (vk.kind === 'novalue') {
      sections.novalue.push({ name, novalue: vk.novalue });
      stats.novalue += 1;
      continue;
    }
    const flag = labFlagOf(x, vk);
    if (!flag) return hold('flag_missing');
    const range = labFormatRange(x.ref_min, x.ref_max, vk.kind === 'numeric' ? vk.value : null, x.is_abnormal);
    const rmin = labLimit(x.ref_min), rmax = labLimit(x.ref_max);
    if (rmin.n != null && rmax.n != null) stats.ranges.both += 1;
    else if (rmin.n != null) stats.ranges.min += 1;
    else if (rmax.n != null) stats.ranges.max += 1;
    else if (range) stats.ranges.text += 1; else stats.ranges.none += 1;
    // Counted for the run summary: each one is a test where reading a bare bound as strict was wrong.
    if (range !== labFormatRange(x.ref_min, x.ref_max)) stats.ranges.at_bound += 1;
    if (flag !== 'in range') {
      if (vk.kind === 'numeric' && (flag === 'HIGH' || flag === 'LOW')) stats.flags.numeric += 1;
      else if (flag === 'HIGH' || flag === 'LOW') stats.flags.phrase += 1; else stats.flags.generic += 1;
    }
    const unit = vk.kind === 'numeric' || vk.kind === 'comparator' ? labUnit(x.units) : '';
    const row = { name, value: vk.value, unit, flag, range, comment: !!x.has_note, corrected: p.corrected };
    const key = flag !== 'in range' ? 'outside' : system;
    sections[key].push(row);
    stats.shown += 1;
    if (flag !== 'in range') stats.outside += 1;
    stats.groups[key] = (stats.groups[key] || 0) + 1;
  }
  hidden += hiddenKeys.size + withheld.length;
  stats.hidden = hidden;
  if (stats.shown > LAB_MAX_RESULTS) return hold('too_many');
  if (!stats.shown) return hold('empty');
  // Newest date on the order, for the release's age limit.
  const resDays = [];
  for (const g of bySpecimen.values()) resDays.push(labDay(g.resulted_date) || labDay(g.collected_date));
  stats.newest_day = resDays.filter(Boolean).sort().pop() || null;
  const byName = (a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0);
  const planOrder = (r) => LAB_PLAN.findIndex((q) => q.name === r.name);
  const model = {
    collected: labCollected(days),
    total: stats.shown,
    outside: stats.outside,
    hidden,
    pending: stats.pending,
    sections: LAB_SECTIONS.filter((s) => sections[s.key].length).map((s) => ({
      key: s.key,
      label: s.label,
      rows: sections[s.key].slice().sort(s.key === 'plan' ? (a, b) => planOrder(a) - planOrder(b) : byName),
    })),
  };
  return { ok: true, model, stats, critical: stats.critical };
}
// LAB-CARD-MODEL-END

// LAB-CARD-BODY-START
function labSummaryLine(total, outside) {
  if (total === 1) return outside ? "The result is outside the lab's range. " + LAB_NOT_A_PROBLEM : "The result is within the lab's range.";
  if (!outside) return 'All ' + total + " results are within the lab's range.";
  return outside + ' of ' + total + ' results ' + (outside === 1 ? 'is' : 'are') + " outside the lab's range. " + LAB_NOT_A_PROBLEM;
}
function labHiddenLine(n) {
  return n === 1
    ? '1 more result is not shown here. Your care team will go over it with you.'
    : n + ' more results are not shown here. Your care team will go over them with you.';
}
function labRowLine(r) {
  if (r.novalue) return '- ' + r.name + ': ' + r.novalue;
  return '- ' + r.name + ': ' + r.value + (r.unit ? ' ' + r.unit : '') + ' (' + r.flag
    + (r.range ? '; lab range: ' + r.range : '') + (r.comment ? '; lab comment' : '') + (r.corrected ? '; corrected by the lab' : '') + ')';
}
// Every note line carries "> " (a blank one is ">"), so no line of a note can pass for card structure.
function labNoteLines(note) {
  return [LAB_NOTE_HEADING + (note.name ? ' (' + note.name + ')' : '')].concat(note.text.split('\n').map((l) => (l ? '> ' + l : '>')));
}
function renderLabBody(model) {
  const lines = [LAB_HEADER, LAB_PROVENANCE + (model.collected ? ' Collected ' + model.collected + '.' : ''), labSummaryLine(model.total, model.outside)];
  if (model.note) lines.push('', ...labNoteLines(model.note));
  for (const s of model.sections) {
    lines.push('', s.label + ' (' + s.rows.length + ')');
    for (const r of s.rows) lines.push(labRowLine(r));
  }
  if (model.pending) lines.push('', LAB_PENDING_LINE);
  if (model.hidden) lines.push('', labHiddenLine(model.hidden));
  lines.push('', LAB_FOOTER[0], LAB_FOOTER[1]);
  return lines.join('\n');
}
// LAB-CARD-BODY-END

// LAB-CARD-PARSE-START
// Strict: the body WF72 renders, or null (the caller shows the plain text). A row's source and
// msg_type are a label, not a trust boundary: everything parsed is escaped by the renderer.
function isLabCardRow(row) {
  return !!row && String(row.msg_type || '') === 'lab_results' && String(row.source || '') === 'system'
    && String(row.direction || '') === 'outbound';
}
// A unit only ever follows a number: a text value ("Not detected") is never split into value + unit.
const LAB_ROW_RE = /^- ([^:\n]{1,60}): (?:((?:[<>]=?)?-?\d{1,16}(?:\.\d{1,8})?)(?: ([^()\n;:]{1,24}))?|([^()\n;:]{1,32})) \((HIGH|LOW|OUTSIDE RANGE|in range)(?:; lab range: ([^()\n;:]{1,40}))?(; lab comment)?(; corrected by the lab)?\)$/;
const LAB_NOVALUE_RE = /^- ([^:\n]{1,60}): (not performed|see lab comment)$/;
function parseLabResultsBody(body) {
  if (typeof body !== 'string' || body.length > 120000) return null;
  const L = body.split('\n');
  if (L.length < 7 || L[0] !== LAB_HEADER) return null;
  const prov = /^Results from Quest Diagnostics, as reported by the lab\.(?: Collected ([A-Za-z0-9 ,]{5,40})\.)?$/.exec(L[1]);
  if (!prov) return null;
  if (L[L.length - 2] !== LAB_FOOTER[0] || L[L.length - 1] !== LAB_FOOTER[1] || L[L.length - 3] !== '') return null;
  const model = { collected: prov[1] || null, total: 0, outside: 0, hidden: 0, pending: false, sections: [] };
  let i = 3, lastIdx = -1;
  const end = L.length - 3;
  while (i < end) {
    if (L[i] !== '') return null;
    i += 1;
    // The pending line comes after every section and before the hidden-results line.
    if (L[i] === LAB_PENDING_LINE) {
      if (model.pending) return null;
      model.pending = true;
      i += 1;
      continue;
    }
    // At most one note, and only as the first block: its lines must be exactly what the renderer writes.
    const nh = /^NOTE FROM YOUR CARE TEAM(?: \(([^()\n]{1,40})\))?$/.exec(L[i] || '');
    if (nh) {
      if (i !== 4 || model.note) return null;
      const name = nh[1] === undefined ? null : labNoteName(nh[1]);
      if (nh[1] !== undefined && name !== nh[1]) return null;
      const parts = [];
      let j = i + 1;
      while (j < end && L[j] !== '') {
        if (L[j] === '>') parts.push('');
        else if (L[j].indexOf('> ') === 0 && L[j].length > 2) parts.push(L[j].slice(2));
        else return null;
        j += 1;
      }
      const text = parts.join('\n');
      if (!labNoteOk(text)) return null;
      model.note = name ? { name, text } : { text };
      i = j;
      continue;
    }
    const hid = /^(\d{1,3}) more results? (?:is|are) not shown here\. Your care team will go over (?:it|them) with you\.$/.exec(L[i] || '');
    if (hid) {
      if (i + 1 !== end) return null;
      model.hidden = Number(hid[1]);
      if (labHiddenLine(model.hidden) !== L[i]) return null;
      i += 1;
      break;
    }
    const h = /^([A-Z][A-Z' ]{2,40}) \((\d{1,3})\)$/.exec(L[i] || '');
    if (!h || model.pending) return null;
    const idx = LAB_SECTIONS.findIndex((s) => s.label === h[1]);
    if (idx <= lastIdx) return null;
    lastIdx = idx;
    const sec = LAB_SECTIONS[idx];
    const n = Number(h[2]);
    if (!n) return null;
    const rows = [];
    for (let k = 1; k <= n; k++) {
      const line = L[i + k];
      if (line == null) return null;
      if (sec.key === 'novalue') {
        const m = LAB_NOVALUE_RE.exec(line);
        if (!m) return null;
        rows.push({ name: m[1], novalue: m[2] });
      } else {
        const m = LAB_ROW_RE.exec(line);
        if (!m) return null;
        const flag = m[5];
        if ((sec.key === 'outside') !== (flag !== 'in range')) return null;
        rows.push({ name: m[1], value: m[2] != null ? m[2] : m[4], unit: m[3] || '', flag, range: m[6] || '', comment: !!m[7], corrected: !!m[8] });
        model.total += 1;
        if (flag !== 'in range') model.outside += 1;
      }
    }
    model.sections.push({ key: sec.key, label: sec.label, rows });
    i += n + 1;
  }
  if (i !== end) return null;
  if (!model.total || L[2] !== labSummaryLine(model.total, model.outside)) return null;
  return model;
}
// LAB-CARD-PARSE-END

// LAB-CARD-NEUTRALIZE-START
// Lab values never reach a model or the care-team Google Chat: the card row, or text that is plainly a
// pasted card, becomes one placeholder. Anything else comes back byte for byte (prompts are pinned).
const LAB_RESULTS_PLACEHOLDER = '[lab results shared with the patient]';
const LAB_CARD_MARKS = [/your lab results are ready/i, /as reported by the lab/i, /(outside|within) the lab'?s range/i, /lab range:/i];
function isLabResultsRow(r) { return !!r && typeof r === 'object' && String(r.msg_type || '') === 'lab_results'; }
// A result line as the card writes it: "- Name: value (FLAG; lab range: ...)".
// Bounded whitespace (no nested \s*) and a length cap keep this linear on a long pasted line.
const LAB_RESULT_LINE = /^[ \t]{0,8}-?[ \t]{0,8}[^:\n]{1,80}:[^\n]{0,240}\((?:HIGH|LOW|OUTSIDE RANGE|in range)\b[^\n]{0,120}\)[ \t]{0,8}$/i;
const LAB_RANGE_FRAGMENT = /lab range:/i;
function neutralizeLabResults(text) {
  if (typeof text !== 'string' || !text) return text;
  // NFKC first and format/control characters deleted: a zero-width character must not split a marker.
  const n = text.normalize('NFKC').replace(/[\p{Cf}\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/gu, '');
  // The whole card pasted back: the header and the provenance line together.
  if (LAB_CARD_MARKS[0].test(n) && LAB_CARD_MARKS[1].test(n)) return LAB_RESULTS_PLACEHOLDER;
  // Otherwise only the pasted result lines go; the patient's own words stay.
  const lines = n.split('\n');
  let changed = false;
  const out = lines.map((l) => (l.length <= 400 && (LAB_RESULT_LINE.test(l) || LAB_RANGE_FRAGMENT.test(l)) ? (changed = true, LAB_RESULTS_PLACEHOLDER) : l));
  return changed ? out.join('\n') : text;
}
function neutralizeLabRow(row) {
  if (!row || typeof row !== 'object') return row;
  if (isLabResultsRow(row)) return Object.assign({}, row, { body: LAB_RESULTS_PLACEHOLDER });
  if (typeof row.body === 'string') {
    const b = neutralizeLabResults(row.body);
    if (b !== row.body) return Object.assign({}, row, { body: b });
  }
  return row;
}
// LAB-CARD-NEUTRALIZE-END

// LAB-CARD-SMS-START
const LAB_PING_ROW_BODY = 'Your lab results are ready - open the app to see them.';
// The link comes from the caller ($vars.PATIENT_APP_URL, S-27) and must be one of our two app links:
// a variable edited to another host would turn our most trusted text into a phishing lure.
const LAB_APP_URL_RE = /^https:\/\/within\.gs\/access-patient(-beta)?$/;
function labPingSms(appUrl) {
  if (typeof appUrl !== 'string' || !LAB_APP_URL_RE.test(appUrl)) return null;
  return 'Your lab results are ready. Open the Withings app to see them in your care team messages: '
    + appUrl + '\n\n- Withings Medical Care Team\n\nReply STOP to opt out.';
}
// LAB-CARD-SMS-END

// LAB-CARD-DECIDE-START
// WF72's release decision, pure. Every doubt holds: the care team has the results in Elation anyway.
// The text goes with the card at any hour: no local-time window, no time zone.
const LAB_RELEASE = { chatCap: 10, pingCap: 10, pingRetryHours: 72, maxFailedPings: 3, trigger: 'wf72_lab_release' };
function labRowsOf(env, uid) {
  if (!env || env.status !== 'success' || !Array.isArray(env.data)) return null;
  const count = Number(env.meta && env.meta.count);
  if (Number.isFinite(count) && count > env.data.length) return null;
  if (env.data.some((r) => !r || String(r.withings_user_id) !== String(uid))) return null;
  return env.data;
}
// Strict: our rows always carry the period, so a row without one cannot block or count.
function labSamePeriod(r, cp) {
  const v = r.id_care_period != null ? r.id_care_period : r.care_period_id;
  return v != null && String(v) === String(cp);
}
const LAB_OPEN_TASK = ['pending', 'scheduled', 'in_progress', 'overdue'];
function labSame(a, b) { return JSON.stringify(a) === JSON.stringify(b); }
function decideRelease(input) {
  const out = { chat: [], pings: [], markers: [], chat_lines: [], held: {}, counts: { candidates: 0, chat: 0, pings: 0, already: 0, capped: 0, markers: 0, note_dropped: 0 }, agg: null };
  // Run-level sums only: per-candidate stats in a pilot would be linkable to one person.
  const agg = { built: 0, results: 0, shown: 0, outside: 0, hidden: 0, sensitive: 0, unplaced: 0, novalue: 0, vitals: 0, withheld: 0, pending: 0,
    ranges: { both: 0, min: 0, max: 0, text: 0, none: 0, at_bound: 0 }, flags: { numeric: 0, phrase: 0, generic: 0 }, body_len_max: 0, unplaced_loincs: {} };
  out.agg = agg;
  const cands = input.candidates || [];
  const hold = (reason) => { out.held[reason] = (out.held[reason] || 0) + 1; };
  out.counts.candidates = cands.length;
  const guards = input.guards || [], pingEnvs = input.pings || [];
  if (guards.length !== cands.length || pingEnvs.length !== cands.length) { for (let i = 0; i < cands.length; i++) hold('guard_unpaired'); return out; }
  const crit = input.critical;
  const critRows = crit && crit.status === 'success' && Array.isArray(crit.data)
    && !(Number.isFinite(Number(crit.meta && crit.meta.count)) && Number(crit.meta.count) > crit.data.length) ? crit.data : null;
  const nowMs = Date.parse(input.now);
  for (let i = 0; i < cands.length; i++) {
    const c = cands[i];
    const uid = String(c.withings_user_id), cp = String(c.care_period_id);
    const m = c.release || null;
    // Only a request for THIS Done counts (WF17 wrote it; a stale or reshaped one is ignored).
    if (!labReleaseMarkerOk(m) || m.state === 'expired') { hold('no_request'); continue; }
    if (!labSameInstant(m.since, c.review_completed_at)) { hold('request_stale'); continue; }
    // A pilot list (LAB_RESULTS_RELEASE='only:<uid>,...') releases to those patients only; no marker.
    if (Array.isArray(input.pilot) && input.pilot.indexOf(uid) < 0) { hold('not_pilot'); continue; }
    const frozen = Array.isArray(m.reports) && m.reports.length ? m.reports.map(String) : labReportsAt(c.grids, m.since);
    const mark = (state, reason, extra) => {
      const keep = state !== 'released' && frozen.length ? { reports: frozen } : {};
      const next = Object.assign({ state, since: m.since }, reason ? { reason } : {}, keep, extra || {}, { at: input.now });
      // A held patient whose reason did not change is not rewritten every hour.
      if (state === 'held' && m.state === 'held' && m.reason === reason && (m.reports || []).length) return;
      if (!labReleaseNextOk(m, next, c.review_completed_at)) return;
      out.markers.push({ task_id: String(c.review_task_id), withings_user_id: uid, care_period_id: cp, lab_release: next });
    };
    // Only our own row for THIS order counts: a forged or older row neither blocks the release nor anchors the SMS.
    const isOurs = (r) => String(r.msg_type || '') === 'lab_results' && String(r.source || '') === 'system'
      && String(r.status || '') === 'sent' && labSamePeriod(r, cp) && String(r.trigger_source || '') === LAB_RELEASE.trigger
      && String(r.payload_ref || '') === 'elation_order:' + String(c.elation_order_id);
    // The row exists but the marker never said so (its write failed): repair it. The note is reported only when the card
    // that went out carries this very note; left-off tests only when the frozen reports rebuild that very card.
    const repair = (row) => {
      const sent = parseLabResultsBody(String(row.body || ''));
      const relNote = labNoteForRelease(c.note, m.since);
      const noteWent = !!(sent && sent.note && relNote && labSame(sent.note, labNoteModelOf(relNote)));
      const core = sent ? Object.assign({}, sent) : null;
      if (core) delete core.note;
      const again = buildReleaseModel({ grids: c.grids, truncated: c.truncated, foreign: c.foreign, doneAt: m.since, reports: frozen.length ? frozen : undefined });
      const left = core && labSame(again.model, core) && again.stats.withheld_tests.length ? { withheld: again.stats.withheld_tests } : {};
      mark('released', null, Object.assign({ order: String(c.elation_order_id), reports: frozen.length ? frozen : ['0'] }, noteWent ? { note_at: relNote.at } : {}, left));
    };
    const age = nowMs - Date.parse(m.since);
    if (m.state !== 'released' && age > LAB_RELEASE_WINDOW_MS) {
      // A card that went out is never announced as not shared: its marker is repaired instead.
      const early = labRowsOf(guards[i], uid);
      const went = early ? early.find(isOurs) : null;
      if (went) { out.counts.already += 1; repair(went); continue; }
      hold('expired');
      mark('expired', m.reason || 'window');
      out.chat_lines.push({ kind: 'expired', withings_user_id: uid, care_period_id: cp });
      continue;
    }
    if (c.order_cancelled) { hold('order_cancelled'); mark('held', 'order_cancelled'); continue; }
    if (!critRows) { hold('critical_read_degraded'); mark('held', 'critical_read_degraded'); continue; }
    const mineCrit = critRows.filter((t) => String(t.withings_user_id) === uid
      || String(t.care_period_id != null ? t.care_period_id : t.id_care_period) === cp);
    if (mineCrit.some((t) => LAB_OPEN_TASK.indexOf(String(t.status || '')) >= 0)) { hold('critical_open'); mark('held', 'critical_open'); continue; }
    if (!(input.consent || {})[cp]) { hold('no_consent'); mark('held', 'no_consent'); continue; }
    const gRows = labRowsOf(guards[i], uid);
    if (!gRows) { hold('guard_degraded'); mark('held', 'guard_degraded'); continue; }
    const sentRow = gRows.find(isOurs);
    let writeNow = false;
    if (!sentRow) {
      if (m.state === 'released') { hold('released_without_row'); continue; }
      // What the care team reviewed: the results final at Done, and on a retry exactly the same reports.
      const built = buildReleaseModel({ grids: c.grids, truncated: c.truncated, foreign: c.foreign, doneAt: m.since, reports: frozen.length ? frozen : undefined });
      const st = built.stats;
      agg.built += 1;
      for (const k of ['results', 'shown', 'outside', 'hidden', 'sensitive', 'unplaced', 'novalue', 'vitals', 'withheld']) agg[k] += Number(st[k] || 0);
      if (st.pending) agg.pending += 1;
      for (const k of Object.keys(agg.ranges)) agg.ranges[k] += Number((st.ranges || {})[k] || 0);
      for (const k of Object.keys(agg.flags)) agg.flags[k] += Number((st.flags || {})[k] || 0);
      for (const l of (st.unplaced_loincs || [])) agg.unplaced_loincs[l] = (agg.unplaced_loincs[l] || 0) + 1;
      // A critical value is released only after a clinician closed its alert: the care team calls first.
      const reportIds = new Set((c.grids || []).map((g) => String(g.report_id || '')).filter(Boolean));
      const reviewedCritical = mineCrit.some((t) => String(t.status || '') === 'completed' && reportIds.has(String(t.trigger_ref_id || '')));
      if ((built.critical || c.critical_now) && !reviewedCritical) { hold('critical_unreviewed'); mark('held', 'critical_unreviewed'); continue; }
      if (!built.ok) { hold(built.reason); mark('held', LAB_HOLD_REASONS.indexOf(built.reason) >= 0 ? built.reason : 'empty'); continue; }
      // The care team's note rides the card only with its own switch on; the card never waits for it.
      const relNote = input.noteRelease === true ? labNoteForRelease(c.note, m.since) : null;
      let noteModel = relNote ? labNoteModelOf(relNote) : null;
      let model = noteModel ? Object.assign({}, built.model, { note: noteModel }) : built.model;
      let body = renderLabBody(model);
      let back = parseLabResultsBody(body);
      if (noteModel && (!back || !labSame(back, model))) {
        out.counts.note_dropped += 1;
        noteModel = null; model = built.model; body = renderLabBody(model); back = parseLabResultsBody(body);
      }
      if (!back || !labSame(back, model)) { hold('unparseable'); mark('held', 'unparseable'); continue; }
      agg.body_len_max = Math.max(agg.body_len_max, body.length);
      if (out.chat.length >= LAB_RELEASE.chatCap) { out.counts.capped += 1; continue; }
      out.chat.push({
        withings_user_id: uid, care_period_id: cp, task_id: String(c.review_task_id), channel: 'portal', direction: 'outbound',
        source: 'system', msg_type: 'lab_results', trigger_source: LAB_RELEASE.trigger, recipient_role: 'patient',
        status: 'sent', sent_at: input.now, subject: noteModel ? 'lab_results v3' : 'lab_results v2', payload_ref: 'elation_order:' + String(c.elation_order_id), body,
        // Written only once the chat row is confirmed (the graph's Chat Write Result).
        marker: Object.assign({ state: 'released', since: m.since, order: String(c.elation_order_id), reports: st.reports.slice(0, 40), at: input.now },
          noteModel ? { note_at: relNote.at } : {}, st.withheld_tests.length ? { withheld: st.withheld_tests } : {}),
      });
      if (input.mode === 'plan') out.chat_lines.push({ kind: 'plan', withings_user_id: uid, care_period_id: cp, note: !!noteModel });
      writeNow = true;
    } else {
      out.counts.already += 1;
      if (m.state !== 'released') repair(sentRow);
    }
    // The ping: only when the chat row exists (or is being written now), never twice, at most 3 failures.
    const pRows = labRowsOf(pingEnvs[i], uid);
    if (!pRows) { hold('ping_guard_degraded'); continue; }
    const mine = pRows.filter((r) => String(r.msg_type || '') === 'chat_notification' && String(r.trigger_source || '') === LAB_RELEASE.trigger && labSamePeriod(r, cp));
    if (mine.some((r) => String(r.status || '') === 'sent')) continue;
    if (mine.filter((r) => String(r.status || '') === 'failed').length >= LAB_RELEASE.maxFailedPings) { hold('ping_gave_up'); continue; }
    if (!writeNow) {
      const at = Date.parse(sentRow.sent_at || sentRow.created || '');
      if (!Number.isFinite(at) || nowMs - at > LAB_RELEASE.pingRetryHours * 3600000) { hold('ping_window_closed'); continue; }
    }
    if (!(input.hasPhone || {})[uid]) { hold('no_phone'); continue; }
    if ((input.smsOptOut || {})[uid]) { hold('sms_opt_out'); continue; }
    const message = labPingSms(input.appUrl);
    if (!message) { hold('no_app_url'); continue; }
    if (out.pings.length >= LAB_RELEASE.pingCap) { out.counts.capped += 1; continue; }
    out.pings.push({ withings_user_id: uid, care_period_id: cp, requires_chat: writeNow, message, row_body: LAB_PING_ROW_BODY });
  }
  out.counts.chat = out.chat.length;
  out.counts.pings = out.pings.length;
  out.counts.markers = out.markers.length;
  // A run of one patient (a targeted run, or an hour with one candidate) would make these counts that patient's.
  if (cands.length < 2) { agg.hidden = null; agg.sensitive = null; agg.unplaced = null; agg.unplaced_loincs = null; agg.withheld = null; }
  // Plan mode counts and writes nothing: no row, no text, no marker. Its Chat line says what would go.
  if (input.mode !== 'live') {
    out.planned = { chat: out.chat.length, pings: out.pings.length, markers: out.markers.length };
    out.chat = []; out.pings = []; out.markers = [];
    out.chat_lines = out.chat_lines.filter((x) => x.kind === 'plan');
  } else {
    out.chat_lines = out.chat_lines.filter((x) => x.kind === 'expired');
  }
  return out;
}
// LAB-CARD-DECIDE-END

export {
  LAB_HEADER, LAB_PROVENANCE, LAB_FOOTER, LAB_SECTIONS, LAB_PLAN, LAB_RACE_EGFR, LAB_SENSITIVE, LAB_FLAGS, LAB_NO_VALUE,
  LAB_NOT_A_PROBLEM, LAB_PENDING_LINE, labHiddenLine, labSummaryLine,
  LAB_RELEASE_WINDOW_MS, LAB_RELEASE_STATES, LAB_HOLD_REASONS, LAB_WITHHELD_MAX, LAB_VITALS_LOINC, labIsVital, LAB_DRUG, labReportsAt, labReleaseModeFor, labSameInstant, labReleaseMarkerOk, labReleaseNextOk, labReleaseRequestOf,
  LAB_NOTE_HEADING, LAB_NOTE_LIMITS, labNoteClean, labNoteOk, labNoteName,
  labNoteStoredOk, labNoteFrozen, labNoteCurrent, labNoteForRelease, labNoteModelOf, labNoteSentAt,
  labClean, labName, labSystem, labFormatRange, labRangeLimits, labCollected, labBar, labPlanKey,
  projectReleaseGrids, buildReleaseModel, renderLabBody, parseLabResultsBody, isLabCardRow,
  LAB_RESULTS_PLACEHOLDER, isLabResultsRow, neutralizeLabResults, neutralizeLabRow,
  LAB_PING_ROW_BODY, labPingSms, LAB_RELEASE, decideRelease,
};
